// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2023-12-21 21:20:05 +0100 (Thu, 21 Dec 2023) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 139864 $
//  Purpose     : call job apis
// =============================================================================
// ---Third-party imports
import {put, takeEvery, select, fork, take, call, race, delay} from 'redux-saga/effects'
import now from 'performance-now'

// ---Framework imports
import {Actions} from 'uix/registries'
import log from 'uix/logging'
import {dict, mapItems, isArray, callIfFunc, isEqual} from 'ldash'
import Api from 'uix/api'
import {matchServerEvent} from 'uix/sagas'
import {canUseDOM} from 'uix/utils'
// ---Local imports
import {
  loadJobSpec,
  setJobSpec,
  startJob,
  stopJob,
  restartJob,
  jobCreated,
  deleteJobs,
  setActiveJobs,
  updateJobInfo,
  loadJobEvents,
  setJobEvents,
} from './actions'

// ---Globals
const JOB_INFO_INTERVAL = 5000

// ---Exports

export const sagas = {
  loadJobSpec: fork(function*() {
    while (true) {
      yield take(loadJobSpec)
      let {data, error} = yield Api.get('/api/getJobSpec')
      if (data) {
        yield put(setJobSpec(data.jobs))
        break
      } else {
        log.error(error)
      }
    }
  }),

  startJob: takeEvery(startJob, function*({payload}) {
    let {name, params} = payload
    let toString = v => {
      if (isArray(v)) {
        return v.join(',')
      }
      return String(v)
    }

    params = dict(mapItems(params, (k, v) => [k, toString(v)]))
    let {data, error} = yield Api.post('/api/job/start', {name, ...params})
    if (data) {
      yield put(jobCreated(data))
    } else {
      yield put(Actions.errorNotification({error, header: 'Job Start Error'}))
    }
  }),
  restartJob: takeEvery(restartJob, function*({payload}) {
    let {ojid} = payload
    let {data, error} = yield Api.post('/api/job/restart', {ojid})
    if (data) {
      yield put(jobCreated(data))
    } else {
      yield put(Actions.errorNotification({error, header: 'Job Restart Error'}))
    }
  }),
  stopJob: takeEvery(stopJob, function*({payload}) {
    let {ojid, terminate} = payload
    let verb = terminate ? 'terminate' : 'stop'
    let {data, error} = yield Api.post(`/api/job/${verb}`, {ojid})
    if (data) {
      if (data.stopped) {
        yield put(Actions.successNotification({message: 'Job stopped'}))
      } else {
        yield put(Actions.warningNotification({message: 'Job not stopped'}))
      }
    } else if (error) {
      yield put(Actions.errorNotification({error, header: 'Job Stop Error'}))
    }
  }),
  deleteJobs: takeEvery(deleteJobs, function*({payload}) {
    let {ojids, callback} = payload
    let {data, error} = yield call(Api.post, '/api/deleteJob', {id: ojids.join(',')})
    if (error) {
      yield put(Actions.errorNotification({error, header: 'Job Error'}))
    }
    callIfFunc(callback, data, error)
  }),

  watchActiveJobs: fork(function*() {
    let lastJobs = []
    while (true) {
      let {timeout, _started, _finished} = yield race({
        timeout: delay(JOB_INFO_INTERVAL),
        _started: take(matchServerEvent('EJobStarted')),
        _finished: take(matchServerEvent('EJobFinished')),
      })

      let active = yield select(root => root.data.job.active)
      if (_started) {
        active = [...active, _started.payload]
      }
      let ojids = active.map(j => j.ojid)

      if (!timeout) {
        // maybe this is a good idea, maybe not
        if (isEqual(new Set(ojids), new Set(lastJobs))) {
          continue
        }
      }

      let missingJobs = lastJobs.filter(ojid => !ojids.includes(ojid))
      lastJobs = ojids
      ojids = [...ojids, ...missingJobs]
      if (ojids.length > 0) {
        let {data, error} = yield call(Api.post, '/api/job/info', {
          ojids: ojids.join(','),
          resolveDependencies: false,
        })
        if (data) {
          //~ log.debug('update %o', data.jobs)
          yield put(updateJobInfo(data.jobs))
        } else {
          log.error('error during job/info %o', error)
        }
      }
    }
  }),

  loadJobEvents: takeEvery(loadJobEvents, function*({payload}) {
    let {ojid} = payload
    let ts = now()
    let {data, error} = yield call(Api.post, '/api/job/events', {ojid})
    // used for throttleing
    data = {...data, api_stats: {duration: now() - ts, ts: now()}}
    if (error) {
      yield put(Actions.errorNotification({error, header: 'Job Error'}))
    } else {
      yield put(setJobEvents(ojid, data))
    }
  }),

  subscribeJobEvents: takeEvery('SET_USER', function*({payload}) {
    // not in SSR
    if (!canUseDOM) return

    let {user} = payload
    if (user.groups.includes('admin')) {
      yield put(Actions.subscribeServerEvent(['EJobQueued', 'EJobStarted', 'EJobFinished'], true))
    }
    if (['admin', 'qs', 'cata'].some(g => user.groups.includes(g))) {
      let {data, error} = yield call(Api.post, '/api/job/active')
      if (data) {
        yield put(Actions.setActiveJobs(data.jobs))
      }
    }
  }),
}
